/* BOJOURNAL — search.js
   Predictive search through Shopify's Predictive Search API, rendered by the
   predictive-search section: Works (products), Collections, Stories
   (articles) and Pages, each in its own labelled group.

   Artists: Shopify's predictive search does not index metaobjects, so artists
   are matched here from the real Artist entries printed in the overlay
   (name, location, URL). Nothing is invented: no entry, no result.

   Keyboard: ↓ from the field moves into the results, ↑/↓ move between them
   (in reading order, across groups), ↑ from the first returns to the field,
   Enter follows the focused result or submits the full search, Escape
   closes the overlay. The result count is announced politely. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('predictive-search')) return;

  const normalise = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ًͯ-ٰٟ]/g, '') // Latin accents, Arabic tashkeel
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/ـ/g, '') // tatweel
      .trim();

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class PredictiveSearch extends B.Component {
    init() {
      this.overlay = this.closest('modal-panel');
      this.input = this.overlay && this.overlay.querySelector('[data-predictive-input]');
      this.resultsEl = this.querySelector('[data-predictive-results]');
      this.artistsEl = this.querySelector('[data-predictive-artists]');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input) return;

      const index = this.overlay.querySelector('[data-artist-index]');
      try {
        this.artists = index ? JSON.parse(index.textContent) : [];
      } catch (error) {
        this.artists = [];
      }

      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.id || 'PredictiveResults');
      this.input.setAttribute('aria-expanded', 'false');

      this.on(this.input, 'input', () => {
        this.toggleAttribute('data-query', this.input.value.trim().length > 0);
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(this.input.value.trim()), 200);
      });
      this.on(this.input, 'keydown', (event) => {
        if (event.key === 'ArrowDown') {
          const first = this.links()[0];
          if (first) {
            event.preventDefault();
            first.focus();
          }
        }
      });
      this.on(this, 'keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const links = this.links();
        const i = links.indexOf(document.activeElement);
        if (i === -1) return;
        event.preventDefault();
        if (event.key === 'ArrowDown') (links[i + 1] || links[i]).focus();
        else if (i === 0) this.input.focus();
        else links[i - 1].focus();
      });
      this.on(this.overlay, 'panel:close', () => clearTimeout(this.timer));
    }

    links() {
      return [...this.querySelectorAll('a[href]')].filter((a) => a.offsetParent !== null && a.tabIndex !== -1);
    }

    renderArtists(term) {
      if (!this.artistsEl) return 0;
      const query = normalise(term);
      const matches =
        query.length < 2
          ? []
          : this.artists
              .filter((a) => normalise(a.name).includes(query) || normalise(a.location).includes(query))
              .slice(0, 6);
      if (!matches.length) {
        this.artistsEl.innerHTML = '';
        return 0;
      }
      const items = matches
        .map(
          (artist) =>
            `<li><a class="predictive-artists__link" href="${escapeHtml(artist.url)}"><span class="predictive__link">${escapeHtml(
              artist.name
            )}</span>${artist.location ? `<span class="t-meta t-muted">${escapeHtml(artist.location)}</span>` : ''}</a></li>`
        )
        .join('');
      this.artistsEl.innerHTML = `<section class="predictive-artists" aria-labelledby="PredictiveArtists"><h2 id="PredictiveArtists" class="predictive__heading t-meta t-muted">${escapeHtml(
        this.dataset.artistsLabel || 'Artists'
      )}</h2><ul role="list">${items}</ul></section>`;
      return matches.length;
    }

    announce(count) {
      if (!this.status) return;
      const template = count === 0 ? this.dataset.statusNone : this.dataset.statusSome;
      this.status.textContent = (template || '').replace('{{ count }}', count);
    }

    async search(term) {
      const artistCount = this.renderArtists(term);
      if (this.controller) this.controller.abort();
      if (!term) {
        this.resultsEl.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        if (this.status) this.status.textContent = '';
        return;
      }
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,article,page,collection',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'last',
        section_id: this.dataset.section
      });
      try {
        const response = await fetch(`${B.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('.predictive');
        this.resultsEl.innerHTML = fresh ? fresh.outerHTML : '';
        B.reveal(this.resultsEl);
        const count = (fresh ? Number(fresh.dataset.predictiveCount) || 0 : 0) + artistCount;
        this.input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        this.announce(count);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.resultsEl.innerHTML = '';
          this.announce(artistCount);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  B.define('predictive-search', PredictiveSearch);
})();
